/**
 * Home/Landing Page for trAInedup.ai - v2 redesign
 * Main entry point showcasing the platform's value proposition
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Layout } from '../components/common/Layout';
import { ROUTES } from '../utils/constants';
import { SEOHead } from '../components/seo/SEOHead';
import { StructuredData, createOrganizationSchema, createWebSiteSchema } from '../components/seo/StructuredData';
import { HeroAssessmentCard } from '../components/assessment/HeroAssessmentCard';

export const HomePage: React.FC = () => {
  return (
    <Layout>
      <SEOHead
        title="trAInedup.ai – Feeling Left Behind by AI? It's Not Too Late to Catch Up"
        description="Feeling left behind by AI? You're not alone, and it's not too late. trAInedup.ai is your supportive, jargon-free path to catching up. Take our free Skills Assessment and get a personalised AI learning plan. No tech background required."
        canonical="https://trainedup.ai/"
        ogType="website"
        keywords={['learn AI', 'AI for beginners', 'catch up on AI', 'left behind by AI', 'AI training', 'AI courses', 'AI education', 'artificial intelligence']}
      />
      <StructuredData data={createOrganizationSchema()} />
      <StructuredData data={createWebSiteSchema()} />

      {/* ================================================================
          HERO SECTION - two-column layout
          ================================================================ */}
      <section className="py-16 md:py-24" style={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #f0fdf9 100%)' }}>
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* LEFT: headline + copy + proof chips */}
            <div>
              <h1 className="font-montserrat font-bold text-4xl md:text-5xl lg:text-[52px] leading-tight text-neutral-800 mb-6">
                Feeling left behind by AI?{' '}
                <span style={{ color: '#5b6cff' }}>It's not too late to catch up.</span>
              </h1>

              <p className="font-open-sans text-lg md:text-xl text-neutral-600 mb-8 leading-relaxed">
                If it feels like the whole world raced ahead while you were busy
                getting on with life, you're not alone, and you're not behind for
                long. You don't need a tech background; you just need the right
                guide. Take our free Skills Assessment and get a personalised AI
                learning plan built around your goals in under 10 minutes.
              </p>

              {/* Proof chips */}
              <div className="flex flex-wrap gap-3">
                {[
                  '1,000+ people trained',
                  '4.9/5 rating',
                  'No tech background needed',
                ].map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-open-sans font-medium text-neutral-700 bg-white border border-neutral-200 shadow-sm"
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: '#00c2a8' }}
                    />
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            {/* RIGHT: Skills Assessment card */}
            <div className="w-full">
              <HeroAssessmentCard />
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          TRUST BAR - "As seen in"
          ================================================================ */}
      <section className="py-10" style={{ background: '#f1f4f8' }}>
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <span
              className="font-montserrat font-bold text-xs uppercase tracking-widest flex-shrink-0"
              style={{ color: '#9ca3af' }}
            >
              As seen in
            </span>
            <div className="grid grid-cols-3 md:flex md:flex-row gap-6 md:gap-10 items-center opacity-50 grayscale w-full md:w-auto">
              <div className="flex items-center justify-center h-10">
                <img src="/logos/entrepreneur.svg" alt="Entrepreneur" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex items-center justify-center h-10">
                <img src="/logos/popular-science.svg" alt="Popular Science" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex items-center justify-center h-10">
                <img src="/logos/mashable.svg" alt="Mashable" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex items-center justify-center h-10">
                <img src="/logos/neowin.png" alt="Neowin" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex items-center justify-center h-10">
                <img src="/logos/financial-post.svg" alt="Financial Post" className="max-h-full max-w-full object-contain invert" />
              </div>
              <div className="flex items-center justify-center h-10">
                <img src="/logos/msn.svg" alt="MSN" className="max-h-full max-w-full object-contain" />
              </div>
            </div>
          </div>
          {/* SEO: structured plain text for search engines */}
          <p className="sr-only">
            Featured by Entrepreneur, Popular Science, Mashable, Neowin, Financial Post, and MSN
          </p>
        </div>
      </section>

      {/* ================================================================
          PAIN POINTS - "Sound familiar?"
          ================================================================ */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span
              className="font-montserrat font-bold text-xs uppercase tracking-widest"
              style={{ color: '#5b6cff' }}
            >
              Sound familiar?
            </span>
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl mt-3 text-neutral-800">
              You keep hearing "just use AI".{' '}
              <span style={{ color: '#5b6cff' }}>but nobody shows you how</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                emoji: '😵',
                bg: '#fff0f0',
                heading: '"There\'s too much information out there"',
                body: 'Endless articles, videos, and tutorials that leave you more confused the more you read.',
              },
              {
                emoji: '⏰',
                bg: '#fffbeb',
                heading: '"I don\'t have time to learn something new"',
                body: 'You\'re busy running your business or managing your workload. Who has time for lengthy courses?',
              },
              {
                emoji: '🤔',
                bg: '#f0f4ff',
                heading: '"Everyone seems to be using AI except me"',
                body: 'Colleagues, competitors, even friends; it feels like everyone raced ahead and you\'re the only one still trying to work out where to start.',
              },
            ].map(({ emoji, bg, heading, body }) => (
              <div
                key={heading}
                className="rounded-2xl p-8 border border-neutral-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-5 text-2xl"
                  style={{ background: bg }}
                >
                  {emoji}
                </div>
                <h3 className="font-montserrat font-bold text-lg text-neutral-800 mb-3">
                  {heading}
                </h3>
                <p className="font-open-sans text-neutral-600 text-sm leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>

          {/* Dark banner */}
          <div
            className="rounded-2xl p-8 md:p-10 text-center"
            style={{ background: 'linear-gradient(135deg, #5b6cff 0%, #8b97ff 100%)' }}
          >
            <p className="font-montserrat font-bold text-xl md:text-2xl text-white italic mb-6">
              "That's exactly why we built trAIned up."
            </p>
            <Link
              to={ROUTES.register}
              className="inline-block px-8 py-3 rounded-full font-montserrat font-semibold text-sm bg-white hover:bg-neutral-50 transition-colors"
              style={{ color: '#5b6cff' }}
            >
              Start Free →
            </Link>
          </div>
        </div>
      </section>

      {/* ================================================================
          SUCCESS STORIES
          ================================================================ */}
      <section className="py-20" style={{ background: '#f0f4ff' }}>
        <div className="container-custom">
          <div className="text-center mb-14">
            <span
              className="font-montserrat font-bold text-xs uppercase tracking-widest"
              style={{ color: '#5b6cff' }}
            >
              Real people. Real results.
            </span>
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl mt-3 text-neutral-800">
              What happens when people who felt behind{' '}
              <span style={{ color: '#00c2a8' }}>start using AI</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Jenny */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="h-1 w-full" style={{ background: '#5b6cff' }} />
              <div className="p-7 flex flex-col flex-1">
                <p
                  className="font-montserrat font-bold text-2xl mb-1"
                  style={{ color: '#5b6cff' }}
                >
                  Hobby → Brand
                </p>
                <p className="font-montserrat text-xs uppercase tracking-widest text-neutral-400 mb-5">
                  Jenny · Candle Maker → Business Owner
                </p>
                <p className="font-open-sans text-sm italic text-neutral-600 mb-5 flex-1">
                  "I went from making candles at the kitchen table to running a
                  real brand with a website, social presence, and customers in
                  three different countries, all using tools I learned here."
                </p>
                <ul className="space-y-2">
                  {[
                    'Researched her niche and customer base',
                    'Built her website in days with AI',
                    'Localised her product in multiple languages',
                  ].map((step) => (
                    <li key={step} className="flex items-start gap-2 text-sm text-neutral-600">
                      <span style={{ color: '#00c2a8' }} className="mt-0.5">→</span>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Eric */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="h-1 w-full" style={{ background: '#00c2a8' }} />
              <div className="p-7 flex flex-col flex-1">
                <p
                  className="font-montserrat font-bold text-2xl mb-1"
                  style={{ color: '#00c2a8' }}
                >
                  +60% Revenue
                </p>
                <p className="font-montserrat text-xs uppercase tracking-widest text-neutral-400 mb-5">
                  Eric · Language Teacher
                </p>
                <p className="font-open-sans text-sm italic text-neutral-600 mb-5 flex-1">
                  "I used to spend entire evenings planning lessons. Now I
                  reclaim that time for my students, and the content they get
                  is better than anything I made manually."
                </p>
                <ul className="space-y-2">
                  {[
                    'Automated lesson planning and grading',
                    'Created AI-generated flashcards and quizzes',
                    'Generated engaging business podcasts for students',
                  ].map((step) => (
                    <li key={step} className="flex items-start gap-2 text-sm text-neutral-600">
                      <span style={{ color: '#00c2a8' }} className="mt-0.5">→</span>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Paul */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
              <div className="h-1 w-full" style={{ background: '#ff6b6b' }} />
              <div className="p-7 flex flex-col flex-1">
                <p
                  className="font-montserrat font-bold text-2xl mb-1"
                  style={{ color: '#ff6b6b' }}
                >
                  Hours Saved Daily
                </p>
                <p className="font-montserrat text-xs uppercase tracking-widest text-neutral-400 mb-5">
                  Paul · Painter & Business Owner
                </p>
                <p className="font-open-sans text-sm italic text-neutral-600 mb-5 flex-1">
                  "Running a family business means wearing every hat. AI tools
                  have basically given me an extra pair of hands for contracts,
                  scheduling, and client communication."
                </p>
                <ul className="space-y-2">
                  {[
                    'Set up automated contracts and calendar planning',
                    'Simplified accounting with AI tools',
                    'Generated health & safety documents',
                  ].map((step) => (
                    <li key={step} className="flex items-start gap-2 text-sm text-neutral-600">
                      <span style={{ color: '#00c2a8' }} className="mt-0.5">→</span>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          HOW IT WORKS - 3 steps
          ================================================================ */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span
              className="font-montserrat font-bold text-xs uppercase tracking-widest"
              style={{ color: '#5b6cff' }}
            >
              Your journey
            </span>
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl mt-3 text-neutral-800">
              From left behind to back in control{' '}
              <span style={{ color: '#5b6cff' }}>in three simple steps</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Dashed connector - desktop only */}
            <div
              className="hidden md:block absolute top-8 left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-px border-t-2 border-dashed"
              style={{ borderColor: '#d6cccc' }}
              aria-hidden
            />

            {[
              {
                num: '1',
                color: '#5b6cff',
                title: 'Take Your Free Assessment',
                desc: 'Tell us about your goals, your role, and your current AI experience. Takes under 10 minutes.',
                tags: ['Under 10 mins', 'No jargon'],
              },
              {
                num: '2',
                color: '#00c2a8',
                title: 'Get Your Personalised Plan',
                desc: 'Receive a learning plan built around your answers, with specific courses matched to you.',
                tags: ['AI-matched', 'Built for you'],
              },
              {
                num: '3',
                color: '#ff6b6b',
                title: 'Learn in Bite-Sized Steps',
                desc: 'Short, practical lessons that fit around your schedule. Apply skills from day one.',
                tags: ['15–30 min lessons', 'Apply immediately'],
              },
            ].map(({ num, color, title, desc, tags }) => (
              <div key={num} className="text-center flex flex-col items-center relative">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-6 text-white font-montserrat font-bold text-xl shadow-lg relative z-10"
                  style={{ background: color }}
                >
                  {num}
                </div>
                <h3 className="font-montserrat font-bold text-lg text-neutral-800 mb-3">
                  {title}
                </h3>
                <p className="font-open-sans text-sm text-neutral-600 mb-4 leading-relaxed">
                  {desc}
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs font-open-sans font-medium text-white"
                      style={{ background: color }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          TESTIMONIALS - dark background
          ================================================================ */}
      <section className="py-20" style={{ background: '#333333' }}>
        <div className="container-custom">
          <div className="text-center mb-14">
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl text-white">
              What Our Members Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote:
                  'I went from being completely overwhelmed by AI to using it daily in my freelance writing business. The personalised approach made all the difference to me.',
                name: 'Sarah Johnson',
                role: 'Freelance Writer, UK',
                initials: 'SJ',
                color: '#5b6cff',
              },
              {
                quote:
                  'Understanding my needs and showing me what I could achieve straight away was a game changer. I still face many challenges running the restaurant, but planning and admin are no longer among them.',
                name: 'Mike Rodriguez',
                role: 'Restaurant Owner, USA',
                initials: 'MR',
                color: '#00c2a8',
              },
              {
                quote:
                  'Finally, AI training that doesn\'t assume I\'m a tech expert! The short courses which are updated monthly are perfect for my busy schedule.',
                name: 'Lisa Thompson',
                role: 'Marketing Consultant, Aus',
                initials: 'LT',
                color: '#ff6b6b',
              },
            ].map(({ quote, name, role, initials, color }) => (
              <div
                key={name}
                className="rounded-2xl p-7 flex flex-col gap-4"
                style={{ background: '#444444' }}
              >
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: '#00c2a8' }}>★</span>
                  ))}
                </div>
                <p className="font-open-sans text-sm text-neutral-300 italic leading-relaxed flex-1">
                  "{quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-montserrat font-bold text-sm text-white"
                    style={{ background: color }}
                  >
                    {initials}
                  </div>
                  <div>
                    <p className="font-montserrat font-semibold text-sm text-white">{name}</p>
                    <p className="font-open-sans text-xs text-neutral-400">{role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          FINAL CTA
          ================================================================ */}
      <section className="py-20" style={{ background: '#f0f4ff' }}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl text-neutral-800 mb-6">
              It's not too late. Your free assessment takes{' '}
              <span style={{ color: '#5b6cff' }}>10 minutes</span>
            </h2>
            <p className="font-open-sans text-lg text-neutral-600 mb-10 leading-relaxed">
              Join thousands of people who once felt left behind and are now using
              AI to save time, grow their income, and get ahead. Register for free
              and get your personalised plan today.
            </p>
            <Link
              to={ROUTES.register}
              className="inline-flex items-center gap-2 px-10 py-4 rounded-full text-white font-montserrat font-bold text-lg shadow-lg hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #5b6cff, #00c2a8)' }}
            >
              Get Started Free <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="mt-5 text-sm font-open-sans text-neutral-500">
              No credit card required · Cancel anytime · 7-day money-back guarantee if you upgrade
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};
